import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  User, createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged, GoogleAuthProvider, signInWithPopup,
  signInWithRedirect, getRedirectResult, sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { callAcceptInvite } from '../firebase/membership';
import type { PendingInvite } from '../models/types';
import { formatAuthError, formatInviteError } from '../utils/authErrors';

/* Three outcomes, not two. `none` is the ordinary state for anyone who already belongs to
   an org, so it must never read as an error — whereas `failed` means we genuinely do not
   know whether an invitation exists, which is exactly when sending someone on to create
   their own organization would be the wrong move. */
export type InviteOutcome =
  | { status: 'applied'; orgId: string }
  | { status: 'none' }
  /* Only expired invitations were found (the server retires them). Not an error, but worth
     saying on the setup page so the user asks for a new one instead of creating an org. */
  | { status: 'expired' }
  | { status: 'failed'; message: string }
  /* The user already belongs to a different org. Applying the invite would silently
     reassign them and orphan whatever they created in the old one, so it is held for an
     explicit decision instead. */
  | { status: 'conflict'; invite: PendingInvite; currentOrgId: string; blockedReason?: string };

/* Apply a pending invite after login/signup.

   The lookup, the membership write and the conflict guard all live in the acceptInvite
   callable now — orgId and role decide authorization for everything in the app, so the
   client is no longer trusted to write them (see the users rules in firestore.rules). The
   callable matches the invite against the *token's* email rather than anything passed in,
   which is what stops one account claiming another's invitation.

   Invites match the exact address signed in with, so somebody invited at one address who
   signs in with another — a Google account on a different address, typically — legitimately
   has no invite. That used to be swallowed by a `.catch(() => null)` along with genuine
   failures, so both produced silence and the user simply landed on the create-an-org page. */
async function applyPendingInvite(confirmSwitch = false, inviteId?: string): Promise<InviteOutcome> {
  try {
    return await callAcceptInvite(confirmSwitch, inviteId);
  } catch (err) {
    console.error('acceptInvite failed', err);
    return { status: 'failed', message: formatInviteError(err) };
  }
}

/* Installed (Home Screen) PWA on iOS/iPadOS. Standalone Safari has no popup window and no
   window.opener, so signInWithPopup can never complete there — the sign-in has to leave the page
   and come back (signInWithRedirect). `navigator.standalone` is the iOS-only legacy flag; the
   display-mode media query is the standard one and also covers installed Chrome/Edge. */
export const isStandalonePwa = (): boolean =>
  (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
  (navigator as Navigator & { standalone?: boolean }).standalone === true;

interface AuthContextType {
  currentUser: User | null;
  isLoading: boolean;
  /** Set when a redirect-based sign-in (installed PWA) came back with an error. The login page
   *  shows it, because by then the click handler that started the sign-in is long gone. */
  redirectError: string | null;
  signup: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  /** Resolves when any in-flight signup/login invite application is complete.
   *  OrgContext awaits this before reading from Firestore to avoid a race
   *  where onAuthStateChanged fires before applyPendingInvite writes orgId. */
  awaitSetup: () => Promise<void>;
  /** Outcome of the last invite application, for pages that need to explain why a user
   *  has no organization. Null before any sign-in this session. */
  inviteOutcome: InviteOutcome | null;
  /** Re-run the invite lookup for the signed-in user — for the `failed` case, where the
   *  invite may well exist and we simply could not read it. */
  retryPendingInvite: () => Promise<InviteOutcome>;
  /** Deliberately accept a `conflict` invite, moving the user out of their current org.
   *  Separate from the automatic path precisely because it is destructive to their access. */
  acceptInviteConflict: () => Promise<InviteOutcome>;
  /** Clear the current notice without acting on it. The invite stays pending. */
  dismissInviteOutcome: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Gate promise: OrgContext awaits this before reading Firestore so it never
  // races ahead of applyPendingInvite during signup/login.
  const setupRef = useRef<Promise<void>>(Promise.resolve());
  const [inviteOutcome, setInviteOutcome] = useState<InviteOutcome | null>(null);

  /* Records the outcome rather than discarding it. Still never throws: the user is already
     authenticated by the time this runs, so an invite problem must not look like a failed
     sign-in — it is reported on the page they land on instead. */
  const runPendingInvite = async (confirmSwitch = false, inviteId?: string): Promise<InviteOutcome> => {
    const outcome = await applyPendingInvite(confirmSwitch, inviteId);
    setInviteOutcome(outcome);
    return outcome;
  };

  const signup = async (email: string, password: string) => {
    let settle!: () => void;
    // Set the gate BEFORE createUserWithEmailAndPassword fires onAuthStateChanged
    setupRef.current = new Promise<void>(res => { settle = res; });
    try {
      await createUserWithEmailAndPassword(auth, email, password);
      // Apply invite if one exists — this is what gives new users org access
      await runPendingInvite();
    } finally {
      settle();
    }
  };

  const login = async (email: string, password: string) => {
    let settle!: () => void;
    setupRef.current = new Promise<void>(res => { settle = res; });
    try {
      await signInWithEmailAndPassword(auth, email, password);
      await runPendingInvite();
    } finally {
      settle();
    }
  };

  const logout = async () => { setInviteOutcome(null); await signOut(auth); };

  /* Popup on the web; redirect in an installed PWA, where a popup cannot complete (see
     isStandalonePwa). The redirect path navigates away, so the invite step and the setup gate for
     that case live in the mount effect below, where getRedirectResult picks the sign-in back up.

     Safari note: the redirect result only survives Safari's third-party-cookie partitioning when
     authDomain is the app's own host (REACT_APP_FIREBASE_AUTH_DOMAIN = the Hosting custom domain,
     which serves /__/auth/* itself), not <project>.firebaseapp.com. */
  const loginWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    if (isStandalonePwa()) {
      await signInWithRedirect(auth, provider);   // leaves the page; completion is in the effect
      return;
    }
    let settle!: () => void;
    setupRef.current = new Promise<void>(res => { settle = res; });
    try {
      await signInWithPopup(auth, provider);
      await runPendingInvite();
    } finally {
      settle();
    }
  };

  const retryPendingInvite = async (): Promise<InviteOutcome> => {
    if (!auth.currentUser) return { status: 'none' };
    return runPendingInvite();
  };

  /* The deliberate half of the conflict guard: the same callable, with the confirmation flag
     the server requires before it will overwrite an existing orgId. Only ever reached from a
     UI that has spelled out what access is lost. */
  const acceptInviteConflict = async (): Promise<InviteOutcome> => {
    if (inviteOutcome?.status !== 'conflict' || !auth.currentUser) {
      return inviteOutcome ?? { status: 'none' };
    }
    return runPendingInvite(true, inviteOutcome.invite.id);
  };

  const dismissInviteOutcome = () => setInviteOutcome(null);

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  // Stable identity: this fed OrgContext.loadProfile's useCallback deps, so an inline arrow here
  // re-created loadProfile on every AuthContext render and re-ran the profile read each time —
  // needless churn, and more chances to hit the transient failure that used to wipe the role.
  const awaitSetup = useCallback(() => setupRef.current, []);

  const [redirectError, setRedirectError] = useState<string | null>(null);

  useEffect(() => {
    /* Complete a redirect sign-in if this load is the return trip. The gate is raised FIRST: on
       that return onAuthStateChanged fires with the user before the invite has been applied, and
       OrgContext awaits setupRef so it never reads Firestore ahead of the orgId write. When this
       load is not a redirect return, getRedirectResult resolves null at once and the gate opens. */
    let settle!: () => void;
    setupRef.current = new Promise<void>(res => { settle = res; });
    getRedirectResult(auth)
      .then(async result => { if (result?.user) await runPendingInvite(); })
      .catch(err => { console.error('Redirect sign-in failed', err); setRedirectError(formatAuthError(err)); })
      .finally(settle);

    const unsub = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setIsLoading(false);
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{
      currentUser, isLoading, redirectError, signup, login, logout, loginWithGoogle, resetPassword,
      awaitSetup, inviteOutcome, retryPendingInvite,
      acceptInviteConflict, dismissInviteOutcome,
    }}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
